/* Announcement carousel: accessible, progressively enhanced rotating messages. */
(function () {
  "use strict";

  const singletonKey = "__ilansAnnouncementCarouselController";
  if (window[singletonKey]) {
    window[singletonKey].initAll();
    return;
  }

  const instances = new WeakMap();

  const init = (root) => {
    if (instances.has(root)) return;

    const slides = Array.from(root.querySelectorAll("[data-announcement-slide]"));
    if (!slides.length) return;

    const parsedInterval = Number.parseInt(root.dataset.interval || "6000", 10);
    const interval = Number.isFinite(parsedInterval) && parsedInterval > 0 ? parsedInterval : 6000;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let index = 0;
    let timer = null;
    let focusPaused = false;
    let pointerFocusMode = false;
    let interactionPointerId = null;
    let pointer = null;
    let suppressClickUntil = 0;
    let settleTimer = null;
    let settleIndex = null;

    const resetVisuals = () => {
      root.classList.remove("is-dragging", "is-settling");
      slides.forEach((slide) => {
        slide.classList.remove("is-drag-neighbor");
        slide.style.transform = "translate3d(0px, 0, 0)";
      });
    };

    const show = (nextIndex) => {
      const focusedSlide = slides[index];
      const focusWasInSlide = focusedSlide.contains(document.activeElement);
      index = (nextIndex + slides.length) % slides.length;
      resetVisuals();
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.toggleAttribute("inert", !active);
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", String(!active));
      });
      if (focusWasInSlide && focusedSlide !== slides[index]) root.focus({preventScroll: true});
    };

    const finishSettlement = () => {
      if (settleTimer !== null) window.clearTimeout(settleTimer);
      settleTimer = null;
      const next = settleIndex;
      settleIndex = null;
      if (next !== null) show(next);
      else resetVisuals();
      schedule();
    };

    const positionDrag = (gesture, deltaX) => {
      const delta = Math.max(-gesture.width, Math.min(gesture.width, deltaX));
      const sign = delta < 0 ? -1 : 1;
      const movesForward = gesture.rtl ? sign > 0 : sign < 0;
      const neighborIndex = (index + (movesForward ? 1 : -1) + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-drag-neighbor", slideIndex === neighborIndex);
        slide.style.transform = `translate3d(${slideIndex === index ? delta : slideIndex === neighborIndex ? delta - sign * gesture.width : 0}px, 0, 0)`;
      });
      gesture.delta = delta;
      gesture.neighborIndex = neighborIndex;
      root.classList.add("is-dragging");
    };

    const settle = (gesture, commit) => {
      settleIndex = commit ? gesture.neighborIndex : index;
      if (reducedMotion.matches) {
        finishSettlement();
        return;
      }
      // Flush the current finger position before enabling the snap transition.
      root.getBoundingClientRect();
      root.classList.remove("is-dragging");
      root.classList.add("is-settling");
      const sign = gesture.delta < 0 ? -1 : 1;
      slides[index].style.transform = `translate3d(${commit ? sign * gesture.width : 0}px, 0, 0)`;
      slides[gesture.neighborIndex].style.transform = `translate3d(${commit ? 0 : -sign * gesture.width}px, 0, 0)`;
      settleTimer = window.setTimeout(finishSettlement, 180);
    };

    const stop = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };

    const schedule = () => {
      stop();
      if (slides.length < 2 || reducedMotion.matches || focusPaused || interactionPointerId !== null || settleTimer !== null) return;
      timer = window.setTimeout(() => {
        show(index + 1);
        schedule();
      }, interval);
    };

    const onFocusIn = () => { if (!pointerFocusMode) { focusPaused = true; stop(); } };
    const onFocusOut = (event) => {
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      focusPaused = false; schedule();
    };
    const onKeyboardInput = () => {
      pointerFocusMode = false;
      if (root.contains(document.activeElement)) { focusPaused = true; stop(); }
    };

    const onPointerDown = (event) => {
      if (!event.isPrimary || event.button > 0) return;
      pointerFocusMode = true;
      focusPaused = false;
      interactionPointerId = event.pointerId;
      stop();
      finishSettlement();
      suppressClickUntil = 0;
      if (slides.length < 2) return;
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, dragged: false, delta: 0,
        width: Math.max(1, root.getBoundingClientRect().width), rtl: window.getComputedStyle(root).direction === "rtl" };
    };

    const onPointerMove = (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const deltaX = event.clientX - pointer.x;
      const deltaY = event.clientY - pointer.y;
      if (!pointer.dragged && Math.abs(deltaY) >= 10 && Math.abs(deltaY) >= Math.abs(deltaX)) {
        pointer = null;
        resetVisuals();
        return;
      }
      if (Math.abs(deltaX) >= 10 && Math.abs(deltaX) > Math.abs(deltaY)) pointer.dragged = true;
      if (pointer.dragged) {
        event.preventDefault();
        positionDrag(pointer, deltaX);
      }
    };

    const onPointerUp = (event) => {
      if (interactionPointerId !== event.pointerId) return;
      interactionPointerId = null;
      if (!pointer || pointer.id !== event.pointerId) { schedule(); return; }
      const gesture = pointer;
      pointer = null;
      const deltaX = event.clientX - gesture.x;
      const deltaY = event.clientY - gesture.y;
      const isSwipe = Math.abs(deltaX) >= Math.min(40, gesture.width * 0.15) && Math.abs(deltaX) > Math.abs(deltaY);
      if (gesture.dragged || isSwipe) {
        positionDrag(gesture, deltaX);
        suppressClickUntil = Date.now() + 800;
        settle(gesture, isSwipe);
      }
      schedule();
    };

    const onPointerCancel = (event) => {
      if (interactionPointerId !== event.pointerId) return;
      interactionPointerId = null;
      if (pointer?.dragged) suppressClickUntil = Date.now() + 800;
      pointer = null;
      resetVisuals();
      schedule();
    };

    const onResize = () => {
      if (pointer?.dragged) suppressClickUntil = Date.now() + 800;
      pointer = null;
      interactionPointerId = null;
      finishSettlement();
    };

    const onDragStart = (event) => event.preventDefault();

    const onClick = (event) => {
      if (Date.now() > suppressClickUntil) return;
      suppressClickUntil = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    const onKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest("input, textarea, select, [contenteditable='true']")) return;

      if (event.key === "ArrowRight") {
        event.preventDefault();
        pointer = null;
        finishSettlement();
        const focusIsInActiveSlide = root.contains(document.activeElement)
          && slides[index].contains(document.activeElement);
        show(index + 1);
        if (focusIsInActiveSlide) root.focus({preventScroll: true});
        schedule();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        pointer = null;
        finishSettlement();
        const focusIsInActiveSlide = root.contains(document.activeElement)
          && slides[index].contains(document.activeElement);
        show(index - 1);
        if (focusIsInActiveSlide) root.focus({preventScroll: true});
        schedule();
      }
    };

    const onMotionChange = (event) => {
      if (event.matches) {
        pointer = null;
        interactionPointerId = null;
        finishSettlement();
      }
      schedule();
    };

    root.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKeyboardInput);
    root.addEventListener("dragstart", onDragStart);
    root.addEventListener("click", onClick, true);
    root.addEventListener("focusin", onFocusIn);
    root.addEventListener("focusout", onFocusOut);
    root.addEventListener("keydown", onKeyDown);
    if (reducedMotion.addEventListener) reducedMotion.addEventListener("change", onMotionChange);
    else reducedMotion.addListener(onMotionChange);

    root.classList.add("is-enhanced");
    root.classList.toggle("is-draggable", slides.length > 1);
    show(0);
    schedule();

    instances.set(root, () => {
      stop();
      if (settleTimer !== null) window.clearTimeout(settleTimer);
      settleTimer = null;
      settleIndex = null;
      pointer = null;
      resetVisuals();
      root.classList.remove("is-enhanced", "is-draggable");
      root.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKeyboardInput);
      root.removeEventListener("dragstart", onDragStart);
      root.removeEventListener("click", onClick, true);
      root.removeEventListener("focusin", onFocusIn);
      root.removeEventListener("focusout", onFocusOut);
      root.removeEventListener("keydown", onKeyDown);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener("change", onMotionChange);
      else reducedMotion.removeListener(onMotionChange);
      slides.forEach((slide, slideIndex) => {
        slide.classList.remove("is-active");
        slide.removeAttribute("aria-hidden");
        slide.removeAttribute("inert");
        slide.hidden = slideIndex > 0;
      });
      instances.delete(root);
    });
  };

  const initAll = (scope = document) => {
    if (scope.matches?.("[data-announcement-carousel]")) init(scope);
    scope.querySelectorAll?.("[data-announcement-carousel]").forEach(init);
  };

  const destroyAll = (scope) => {
    const roots = [];
    if (scope.matches?.("[data-announcement-carousel]")) roots.push(scope);
    scope.querySelectorAll?.("[data-announcement-carousel]").forEach((root) => roots.push(root));
    roots.forEach((root) => instances.get(root)?.());
  };

  window[singletonKey] = {initAll};
  initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroyAll(event.target));
})();
